import React, { useImperativeHandle, useRef, useEffect, forwardRef, useState } from "react";
import { Modal } from 'bootstrap';
import uuid from 'react-uuid';
import TapSelect from "../../../components/ui/TapSelect";
import { useSelector } from "react-redux";
import HttpAPICall from "../../../services/HttpAPICall";
import { HRM_API_BASE_URL_2 } from "../../../services/TapApiUrls";
import Loader from "../../../components/ui/Loader/Loader";
import InputEmployeeSearch from "../../includes/ui/InputEmployeeSearch";
import InputAssetSearch from "../../includes/ui/InputAssetSearch";
import InputTicketSearch from "../../includes/ui/InputTicketSearch";
import InputWorkstationSearch from "../../includes/ui/InputWorkstationSearch";
import InputProjectSearch from "../../includes/ui/InputProjectSearch";
import { PeriodSelectionWithCustom } from "../../../components/TapUi";
import moment from "moment";

const WorkLogSearchModal = forwardRef(({ searchHandle, listingLoading, formSearchedElems, initialSearchData, appliedSearchData }, ref) => {

    const access_token = useSelector(state => state.auth.access_token);
    const formDataUrl = HRM_API_BASE_URL_2 + "/work_log/form_data";
    const all_periods = useSelector(state => state.app.all_periods ? state.app.all_periods : []);

    const modalElem = useRef(null);
    const searchModal = useRef(null);
    const modalID = uuid();

    const searchInitState = {
        search_employee_ids: [],
        search_asset_ids: [],
        search_work_log_type_id: null,
        search_workstation_ids: [],
        search_ticket_ids: [],
        search_project_ids: [], // Added project search
        date_range: null,
        date_range_start: null,
        date_range_end: null,
        search_reporting_manager: "",
        search_division_ids: [],
    };

    const searchFormLabel = {
        search_employee_ids: "Employee : ",
        search_asset_ids: "Asset : ",
        search_work_log_type_id: "Work Log Type : ",
        search_workstation_ids: "Workstation : ",
        search_ticket_ids: "Ticket : ",
        search_project_ids: "Project : ", // Added project label
        date_range: "Date Range : ",
        search_reporting_manager: "Reporting Manager : ",
        search_division_ids: "Department : ",
    };

    const [searchForm, setSearchForm] = useState({ ...searchInitState });
    const [searchedEmployees, setSearchedEmployees] = useState([]);
    const [searchedAssets, setSearchedAssets] = useState([]);
    const [searchedTickets, setSearchedTickets] = useState([]);
    const [searchedWorkstations, setSearchedWorkstations] = useState([]);
    const [searchedProjects, setSearchedProjects] = useState([]); // Added project state
    const [managerData, setManagerData] = useState(null);

    const [formData, setFormData] = useState({
        data_loaded: false,
        loading: false,
        work_log_types: [],
        periods: [],
        division: []
    });
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        modalElem.current = document.getElementById(modalID);
        searchModal.current = new Modal(modalElem.current, { keyboard: false, backdrop: false });
        return () => {
            if (searchModal.current) {
                searchModal.current.dispose();
            }
        };
    }, []);

    useEffect(() => {
        if (!formData.data_loaded) {
            formDataApi();
        }
    }, []);

    useEffect(() => {
        if (appliedSearchData && Object.keys(appliedSearchData).length > 0 && formData.data_loaded && !isInitialized) {
            setSearchForm(prev => ({
                ...prev,
                ...appliedSearchData
            }));
            setIsInitialized(true);
        }
    }, [appliedSearchData, formData.data_loaded, isInitialized]);

    useImperativeHandle(ref, () => ({
        initModalHandler: () => {
            openModalHandle();
        },
        resetModalFilters: () => {
            resetAllFilters();
            setIsInitialized(false);
        }
    }));

    const formDataApi = () => {
        setFormData(pre => ({ ...pre, loading: true }));
        HttpAPICall.withAthorization("GET", formDataUrl, access_token, {}, {}, (resp) => {
            const responseData = resp.data;
            setFormData({
                data_loaded: true,
                loading: false,
                work_log_types: responseData?.work_log_type && responseData.work_log_type.length > 0
                    ? responseData.work_log_type.map((s) => ({ value: s.id, label: s.name }))
                    : [],
                periods: [...all_periods, { key: 'custom', display: 'Custom Date Range' }],
                division: responseData?.division && responseData.division.length > 0
                    ? responseData.division.map((div) => ({ value: div.id, label: div.name }))
                    : []
            });
        }).catch(() => {
            setFormData(pre => ({ ...pre, loading: false }));
        });
    };

    const searchModalOpen = () => searchModal.current && searchModal.current.show();
    const searchModalClose = () => {
        searchModal.current && searchModal.current.hide();
    };

    const resetAllFilters = () => {
        setSearchForm({ ...searchInitState });
        setSearchedEmployees([]);
        setSearchedAssets([]);
        setSearchedTickets([]);
        setSearchedWorkstations([]);
        setSearchedProjects([]); // Reset projects
        setManagerData(null);
    };

    const openModalHandle = () => {
        if (appliedSearchData && Object.keys(appliedSearchData).length > 0 && formData.data_loaded) {
            setSearchForm({
                ...searchInitState,
                ...appliedSearchData
            });
            setIsInitialized(true);
        } else {
            resetAllFilters();
            setIsInitialized(false);
        }
        searchModalOpen();
        if (!formData.data_loaded) {
            formDataApi();
        }
    };

    const handleMultipleSelect = (selectedOption, key, setSearchState) => {
        let data = [];
        if (selectedOption && selectedOption.length > 0) {
            data = selectedOption.map(d => d.value);
        }
        setSearchForm(prev => ({
            ...prev,
            [key]: data
        }));
        if (setSearchState) {
            setSearchState(selectedOption && selectedOption.length > 0 ? selectedOption : []);
        }
    };

    const handleSingleSelect = (selectedOption, key) => {
        let data = null;
        if (selectedOption) {
            data = selectedOption.value;
        }
        setSearchForm(prev => ({
            ...prev,
            [key]: data
        }));
    };

    const handlePeriodSelect = (period, startDate, endDate) => {
        setSearchForm(prev => ({
            ...prev,
            date_range: period,
            date_range_start: startDate || null,
            date_range_end: endDate || null
        }));
    };

    const searchFormSubmit = (e = null) => {
        e && e.preventDefault();
        let searchKeys = Object.keys(searchForm);
        let searchedElems = [];
        let searchParams = {};

        searchKeys.forEach((key) => {
            let label = searchFormLabel[key];
            let value = searchForm[key];

            if (value && (Array.isArray(value) ? value.length > 0 : (typeof value === 'string' ? value.toString().length > 0 : value !== null))) {
                searchParams[key] = value;
                if (label) {
                    let show_val = value;

                    if (key === "search_employee_ids") {
                        show_val = searchedEmployees.length > 0 ? searchedEmployees.map(s => s.label).join(", ") : (Array.isArray(value) ? value.join(", ") : value);
                    } else if (key === "search_asset_ids") {
                        show_val = searchedAssets.length > 0 ? searchedAssets.map(s => s.display_label || s.label).join(", ") : (Array.isArray(value) ? value.join(", ") : value);
                    } else if (key === "search_work_log_type_id") {
                        const foundType = formData.work_log_types.find((s) => value === s.value);
                        show_val = foundType ? foundType.label : value;
                    } else if (key === "search_workstation_ids") {
                        show_val = searchedWorkstations.length > 0 ? searchedWorkstations.map(s => s.label).join(", ") : (Array.isArray(value) ? value.join(", ") : value);
                    } else if (key === "search_ticket_ids") {
                        show_val = searchedTickets.length > 0 ? searchedTickets.map(s => s.value).join(", ") : (Array.isArray(value) ? value.join(", ") : value);
                    } else if (key === "search_project_ids") { // Added project display
                        show_val = searchedProjects.length > 0 ? searchedProjects.map(s => s.display_label || s.label).join(", ") : (Array.isArray(value) ? value.join(", ") : value);
                    } else if (key === "search_reporting_manager") {
                        show_val = managerData?.label || value;
                    } else if (key === "search_division_ids") {
                        show_val = formData.division.filter((s) => value.includes(s.value)).map((s) => s.label).join(", ");
                    } else if (key === "date_range") {
                        if (value === 'custom' && searchForm.date_range_start && searchForm.date_range_end) {
                            let start_range = moment(searchForm.date_range_start).format('DD-MMM-YYYY');
                            let end_range = moment(searchForm.date_range_end).format('DD-MMM-YYYY');
                            show_val = `Custom Date Range (${start_range} - ${end_range})`;
                        } else if (value) {
                            const foundPeriod = all_periods.find((s) => s.key === value);
                            show_val = foundPeriod?.display_with_date || value;
                        } else {
                            show_val = null;
                        }
                    }

                    if (show_val && show_val.toString().length > 0 && show_val !== '[]') {
                        searchedElems.push(label + `<b>${show_val}</b>`);
                    }
                }
            }
        });

        searchHandle(searchedElems, searchParams);
        searchModalClose();
    };

    return (
        <>
            <div className="modal fade" id={modalID} tabIndex="-1" ref={modalElem}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Worklog Search</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={searchModalClose} aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            {formData.loading ? (
                                <Loader />
                            ) : (
                                <>
                                    <form onSubmit={searchFormSubmit} id="worklogSearchForm">
                                        <div className="row align-items-center mb-3">
                                            <label className="col-sm-4 col-form-label col-form-label-sm text-start">Employee</label>
                                            <div className="col-sm-8">
                                                <InputEmployeeSearch
                                                    changeEvent={(selectedOption) => {
                                                        handleMultipleSelect(selectedOption, 'search_employee_ids', setSearchedEmployees);
                                                    }}
                                                    isMulti={true}
                                                    containerHeight="30px"
                                                    fontSize="93%"
                                                    placeholder="Select Employee"
                                                    value={searchedEmployees}
                                                />
                                            </div>
                                        </div>
                                        <div className="row align-items-center mb-3">
                                            <label className="col-sm-4 col-form-label col-form-label-sm text-start">Asset</label>
                                            <div className="col-sm-8">
                                                <InputAssetSearch
                                                    changeEvent={(selectedOption) => {
                                                        handleMultipleSelect(selectedOption, 'search_asset_ids', setSearchedAssets);
                                                    }}
                                                    isMulti={true}
                                                    containerHeight="30px"
                                                    fontSize="93%"
                                                    placeholder="Select Asset"
                                                    value={searchedAssets.length > 0 ? searchedAssets.map(a => { a.label = a.display_label; return a; }) : []}
                                                />
                                            </div>
                                        </div>
                                        <div className="row align-items-center mb-3">
                                            <label className="col-sm-4 col-form-label col-form-label-sm text-start">Project</label>
                                            <div className="col-sm-8">
                                                <InputProjectSearch
                                                    changeEvent={(selectedOption) => {
                                                        handleMultipleSelect(selectedOption, 'search_project_ids', setSearchedProjects);
                                                    }}
                                                    placeholder="Search Project"
                                                    isMulti={true}
                                                    containerHeight="30px"
                                                    fontSize="93%"
                                                    value={searchedProjects.length > 0 ? searchedProjects.map(a => { a.label = a.display_label; return a; }) : []}
                                                />
                                            </div>
                                        </div>
                                        <div className="row align-items-center mb-3">
                                            <label className="col-sm-4 col-form-label col-form-label-sm text-start">Work Log Type</label>
                                            <div className="col-sm-8">
                                                <TapSelect
                                                    options={formData.work_log_types}
                                                    changeEvent={(selectedOption) => handleSingleSelect(selectedOption, 'search_work_log_type_id')}
                                                    isSearchable={true}
                                                    isClearable={true}
                                                    menuPlacement="bottom"
                                                    value={formData.work_log_types.find((s) => searchForm.search_work_log_type_id === s.value)}
                                                    placeholder="Select Work Log Type"
                                                    containerHeight="30px"
                                                    fontSize="93%"
                                                    isMulti={false}
                                                />
                                            </div>
                                        </div>
                                        <div className="row align-items-center mb-3">
                                            <label className="col-sm-4 col-form-label col-form-label-sm text-start">Workstation</label>
                                            <div className="col-sm-8">
                                                <InputWorkstationSearch
                                                    placeholder="Please Select Workstation"
                                                    changeEvent={(selectedOption) => {
                                                        handleMultipleSelect(selectedOption, 'search_workstation_ids', setSearchedWorkstations);
                                                    }}
                                                    isMulti={true}
                                                    containerHeight="30px"
                                                    fontSize="93%"
                                                    value={searchedWorkstations}
                                                />
                                            </div>
                                        </div>
                                        <div className="row align-items-center mb-3">
                                            <label className="col-sm-4 col-form-label col-form-label-sm text-start">Ticket</label>
                                            <div className="col-sm-8">
                                                <InputTicketSearch
                                                    containerHeight="30px"
                                                    fontSize="93%"
                                                    changeEvent={(selectedOption) => {
                                                        handleMultipleSelect(selectedOption, 'search_ticket_ids', setSearchedTickets);
                                                    }}
                                                    isMulti={true}
                                                    value={searchedTickets}
                                                />
                                            </div>
                                        </div>
                                        <div className="row align-items-center mb-3">
                                            <label className="col-sm-4 col-form-label col-form-label-sm text-start">Reporting Manager</label>
                                            <div className="col-sm-8">
                                                <InputEmployeeSearch
                                                    placeholder="Search Reporting Manager"
                                                    changeEvent={(selectedOption) => {
                                                        if (selectedOption) {
                                                            setSearchForm(prev => ({
                                                                ...prev,
                                                                search_reporting_manager: selectedOption.value
                                                            }));
                                                            setManagerData(selectedOption);
                                                        } else {
                                                            setSearchForm(prev => ({
                                                                ...prev,
                                                                search_reporting_manager: ""
                                                            }));
                                                            setManagerData(null);
                                                        }
                                                    }}
                                                    containerHeight="30px"
                                                    fontSize="93%"
                                                    value={managerData}
                                                />
                                            </div>
                                        </div>

                                        <div className="row align-items-center mb-3">
                                            <label className="col-sm-4 col-form-label col-form-label-sm text-start">Department</label>
                                            <div className="col-sm-8">
                                                <TapSelect
                                                    options={formData.division}
                                                    changeEvent={(selectedOption) => {
                                                        let data = [];
                                                        if (selectedOption && selectedOption.length > 0) {
                                                            data = selectedOption.map(d => d.value);
                                                        }
                                                        setSearchForm(prev => ({
                                                            ...prev,
                                                            search_division_ids: data
                                                        }));
                                                    }}
                                                    isSearchable={true}
                                                    isClearable={true}
                                                    isMulti={true}
                                                    menuPlacement="bottom"
                                                    value={formData.division.filter((s) => searchForm.search_division_ids?.includes(s.value))}
                                                    placeholder="Select Department"
                                                    containerHeight="30px"
                                                    fontSize="93%"
                                                />
                                            </div>
                                        </div>
                                        <div className="row align-items-center mb-3">
                                            <label className="col-sm-4 col-form-label col-form-label-sm text-start">Date Range</label>
                                            <div className="col-sm-8">
                                                <PeriodSelectionWithCustom
                                                    periods={formData.periods}
                                                    value={searchForm.date_range}
                                                    startDate={searchForm.date_range_start}
                                                    endDate={searchForm.date_range_end}
                                                    className="form-control-sm"
                                                    containerHeight="30px"
                                                    fontSize="93%"
                                                    menuPlacement={"bottom"}
                                                    onSelectPeriod={(period, startDate, endDate) => handlePeriodSelect(period, startDate, endDate)}
                                                />
                                            </div>
                                        </div>
                                    </form>
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" onClick={searchModalClose}>
                                Close
                            </button>
                            <button type="submit" form="worklogSearchForm" className="btn btn-primary">
                                Search
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export default WorkLogSearchModal;